import { useEffect } from 'react';

interface SeoProps {
  /** Page-specific title. Omit on the homepage to use the brand default. */
  title?: string;
  description?: string;
  /** Overrides the canonical path; defaults to the current pathname. */
  canonicalPath?: string;
  image?: string;
  /** Keep thin / transient pages (not-found, filtered views) out of the index. */
  noindex?: boolean;
}

const SITE = 'Souqli';
const BRAND_TITLE = 'Souqli · Curated · Verified · Resold';
const DEFAULT_DESC =
  'Buy and sell verified, pre-loved fashion, tech and more on Souqli, with payments held safely until you confirm and every seller ID-checked.';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Per-page title and meta, set on the client. One source of truth for a page's
 * title, description, canonical URL and Open Graph tags, so each page reads
 * correctly in the browser tab, in search results and when shared, instead of
 * the single static title in index.html appearing on every page.
 */
export function Seo({ title, description, canonicalPath, image, noindex }: SeoProps) {
  const fullTitle = title ? `${title} · ${SITE}` : BRAND_TITLE;
  const desc = description ?? DEFAULT_DESC;

  useEffect(() => {
    document.title = fullTitle;
    upsertMeta('name', 'description', desc);
    upsertMeta('name', 'robots', noindex ? 'noindex,nofollow' : 'index,follow');
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', desc);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', SITE);
    if (image) upsertMeta('property', 'og:image', image);

    const url = window.location.origin + (canonicalPath ?? window.location.pathname);
    upsertLink('canonical', url);
    upsertMeta('property', 'og:url', url);
  }, [fullTitle, desc, canonicalPath, image, noindex]);

  return null;
}

export default Seo;
